import type { VNodeChild } from 'vue';
import type {
	FormSchemaDataField,
	FormSchemaField,
	FormSchemaPresentational,
	FormSchemaSection,
	FormSchemaSections,
	FormSchemaStructuralEntry,
} from '#layers/block-engine/types/form-schema';
import type { FormSchemaChromeSectionItem } from '#layers/block-engine/utils/block/api/config';
import type {
	FormSchemaChromeBlock,
	NormalizedFormSchemaSectionsSingleSlot,
} from '#layers/block-engine/utils/form-schema/chrome';
import { isRecord } from '#layers/base/public';
import {
	getFormSchemaEntryBlock,
	normalizeFormSchemaSections,
	normalizeFormSchemaSectionsChrome,
	requireFormSchemaChrome,
} from '#layers/block-engine/utils/form-schema/chrome';
import {
	getVisibleFormSchemaSections,
	hasVisibleFormSchemaFields,
	isFormSchemaDataField,
	isFormSchemaEntryVisible,
	isFormSchemaPresentationalEntry,
	isFormSchemaSectionEntry,
	isFormSchemaSectionsEntry,
} from '#layers/block-engine/utils/form-schema/entries';

type StructuralSlots = Record<string, (slotProps: Record<string, unknown>) => VNodeChild>;

export type FormSchemaRenderEntry<T extends object> =
	| FormSchemaDataRenderEntry<T>
	| FormSchemaPresentationalRenderEntry
	| FormSchemaSectionRenderEntry<T>
	| FormSchemaSectionsRenderEntry<T>;

export interface FormSchemaDataRenderEntry<T extends object> {
	type: 'data';
	key: string;
	field: FormSchemaDataField<T, string>;
}

export interface FormSchemaPresentationalRenderEntry {
	type: 'presentational';
	key: string;
	block: FormSchemaPresentational['block'];
	props?: Record<string, unknown>;
	slots: StructuralSlots;
}

export interface FormSchemaSectionRenderEntry<T extends object> {
	type: 'section';
	key: string;
	block: FormSchemaChromeBlock;
	props?: Record<string, unknown>;
	slots: StructuralSlots;
	slot: string;
	fields: FormSchemaField<T>[];
}

export interface FormSchemaSectionsRenderEntry<T extends object> {
	type: 'sections';
	key: string;
	block: FormSchemaChromeBlock;
	props: Record<string, unknown>;
	slots: StructuralSlots;
	sections: FormSchemaChromeSectionItem<T>[];
	namedSections: FormSchemaNamedRenderSection<T>[];
	singleSlot?: NormalizedFormSchemaSectionsSingleSlot;
}

export interface FormSchemaNamedRenderSection<T extends object> {
	/** Normalized section item. */
	item: FormSchemaChromeSectionItem<T>;
	/** Vue slot name used to render the section content. */
	slotName: string;
}

/**
 * Resolve a FormSchema entry into the data needed by the renderer.
 */
export function createFormSchemaRenderEntry<T extends object>(
	entry: FormSchemaField<T>,
	index: number,
): FormSchemaRenderEntry<T> | undefined {
	if (isFormSchemaDataField<T>(entry)) {
		return createDataRenderEntry(entry);
	}

	// A structural entry with a missing or incompatible chrome block throws while
	// building its render entry; skip only that entry so the rest of the form still renders.
	try {
		if (isFormSchemaPresentationalEntry(entry)) {
			return createPresentationalRenderEntry(entry, index);
		}

		if (isFormSchemaSectionEntry<T>(entry)) {
			return createSectionRenderEntry(entry, index);
		}

		if (isFormSchemaSectionsEntry<T>(entry)) {
			return createSectionsRenderEntry(entry, index);
		}
	}
	catch (error) {
		warnFormSchemaEntry(entry, '[FormSchemaFields] Ignoring FormSchema entry that cannot be rendered.', error);
		return;
	}

	warnFormSchemaEntry(entry, '[FormSchemaFields] Ignoring FormSchema entry without a name or structural kind.');
}

// Dedup warnings by entry object so re-resolves of a static schema warn once.
const warnedEntries = new WeakSet<object>();

/**
 * Report entries the renderer cannot display instead of dropping them silently.
 */
function warnFormSchemaEntry(entry: unknown, message: string, ...details: unknown[]): void {
	if (isRecord(entry)) {
		if (warnedEntries.has(entry)) {
			return;
		}

		warnedEntries.add(entry);
	}

	console.warn(message, entry, ...details);
}

/**
 * Create a render entry for a visible data field.
 */
function createDataRenderEntry<T extends object>(
	entry: FormSchemaDataField<T, string>,
): FormSchemaDataRenderEntry<T> | undefined {
	if (!isFormSchemaEntryVisible(entry)) {
		return;
	}

	return {
		type: 'data',
		key: entry.name,
		field: entry,
	};
}

/**
 * Create a render entry for visible presentational chrome.
 */
function createPresentationalRenderEntry(
	entry: FormSchemaPresentational,
	index: number,
): FormSchemaPresentationalRenderEntry | undefined {
	if (!isFormSchemaEntryVisible(entry)) {
		return;
	}

	const block = getFormSchemaEntryBlock(entry);

	return {
		type: 'presentational',
		key: `presentational-${index}`,
		block,
		props: entry.props,
		slots: getStructuralSlots(entry),
	};
}

/**
 * Create a render entry for visible single-section chrome.
 */
function createSectionRenderEntry<T extends object>(
	entry: FormSchemaSection<T>,
	index: number,
): FormSchemaSectionRenderEntry<T> | undefined {
	if (!isFormSchemaEntryVisible(entry) || !hasVisibleFormSchemaFields(entry.fields)) {
		return;
	}

	const block = getFormSchemaEntryBlock(entry);
	const chrome = requireFormSchemaChrome(entry.kind, block);

	return {
		type: 'section',
		key: `section-${index}`,
		block,
		props: entry.props,
		slots: getStructuralSlots(entry),
		slot: 'section' in chrome ? chrome.section.slot ?? 'default' : 'default',
		fields: entry.fields,
	};
}

/**
 * Create a render entry for visible multi-section chrome.
 */
function createSectionsRenderEntry<T extends object>(
	entry: FormSchemaSections<T>,
	index: number,
): FormSchemaSectionsRenderEntry<T> | undefined {
	if (!isFormSchemaEntryVisible(entry)) {
		return;
	}

	const visibleSections = getVisibleFormSchemaSections(entry);

	if (visibleSections.length === 0) {
		return;
	}

	const block = getFormSchemaEntryBlock(entry);
	const chrome = requireFormSchemaChrome(entry.kind, block);
	const sectionsChrome = normalizeFormSchemaSectionsChrome('sections' in chrome ? chrome.sections : undefined);
	const sections = normalizeFormSchemaSections({
		entry,
		entryIndex: index,
		sections: visibleSections,
	});
	const hasNamedSlots = Boolean(sectionsChrome.slot);

	// Named slots and the shared fallback are mutually exclusive so nested fields render once.
	return {
		type: 'sections',
		key: `sections-${index}`,
		block,
		props: {
			...entry.props,
			// Chrome-generated props must win so entry props cannot break item-to-slot wiring.
			...sectionsChrome.props({ sections }),
		},
		slots: getStructuralSlots(entry),
		sections,
		namedSections: hasNamedSlots ? createNamedRenderSections(sections, sectionsChrome) : [],
		...(hasNamedSlots ? {} : { singleSlot: sectionsChrome.singleSlot }),
	};
}

/**
 * Read slots supplied directly by the structural entry.
 */
function getStructuralSlots(entry: FormSchemaStructuralEntry): StructuralSlots {
	return entry.slots ?? {};
}

/**
 * Resolve chrome-specific section slot names from normalized items.
 */
function createNamedRenderSections<T extends object>(
	sections: FormSchemaChromeSectionItem<T>[],
	chrome: ReturnType<typeof normalizeFormSchemaSectionsChrome>,
): FormSchemaNamedRenderSection<T>[] {
	return sections.map((section) => ({
		item: section,
		slotName: chrome.slot?.(section) ?? section.slotName,
	}));
}
